import React, { useState } from 'react';
import Image from 'next/image';
import Router, { useRouter } from 'next/router';
import { Container, Row, Col } from 'react-bootstrap';
import { selectCurrentUser } from '@/stores/currentUserApi';
import { selectCartTotal } from '@/stores/cartApi';
import { useAppSelector, type AppRootState } from '@/configureStore';
import { Can } from '@/utils/acl-utils';
import { ShoppingCartButton } from '@/components/ShoppingCartButton';
import templateDataService from '@/services/template-data-service';
import { useFeatureToggles, useIsFeatureEnabled, useShoppingCartRoute } from '@/services/feature-toggles-service';
import { UserArea } from './Components/UserArea';
import { MainNavigation } from './Components/MainNavigation';
import { ToggleMenuButton } from './Components/ToggleMenuButton';
import { mainNavigationData } from './navigation-data';

import styles from './Header.module.scss';

export const MclHeader = () => {
  const cartCount = useAppSelector((state: AppRootState) => selectCartTotal(state));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const router = useRouter();
  const templateData = templateDataService.getTemplateData();
  const currentUser = useAppSelector((state: AppRootState) => selectCurrentUser(state));

  const featureToggles = useFeatureToggles();
  const isFeatureEnabled = (key: string): boolean => {
    const featureToggle = featureToggles[key];
    return featureToggle?.value === '1';
  };

  const mainNavigationItems = mainNavigationData.filter((item) => {
    const hasAccess = !item.security || Can(item.security)(currentUser?.accessControlList);
    if (!hasAccess) {
      return false;
    }

    const shouldDisplay = typeof item.display === 'function' ? item.display(currentUser, useIsFeatureEnabled) : true;
    if (!shouldDisplay) {
      return false;
    }

    return !item.featureToggle || isFeatureEnabled(item?.featureToggle);
  });

  const closeMenu = () => setIsMenuOpen(false);
  Router.events.on('routeChangeComplete', closeMenu);
  Router.events.on('routeChangeError', closeMenu);
  const shoppingCartRoute = useShoppingCartRoute();

  return (
    <header className={`${styles.header} ${styles.mclHeader}`}>
      <Container>
        <Row className="align-items-center">
          <Col>
            <div style={{ height: '12px' }}></div>
          </Col>
        </Row>
        <Row className="align-items-center justify-content-between">
          <Col xs="auto" className="d-lg-none">
            <ToggleMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)} isMenuOpen={isMenuOpen} />
          </Col>
          <Col xs={{ order: 2, span: 'auto' }} xl={{ order: 1 }}>
            <a href="/">
              <Image src={templateData.sector.logo} alt="Carel Lurvink logo" width={60} height={50} className={styles.logo} priority />
            </a>
          </Col>
          <Col xs={{ order: 2, span: 'auto' }} className="d-none d-lg-block ml-lg-auto">
            <UserArea user={currentUser} />
          </Col>
          <Col className="d-flex justify-content-end" xs={{ order: 12, span: 'auto' }}>
            <ShoppingCartButton cartCount={cartCount} size="lg" onClick={() => router.push(shoppingCartRoute)} />
          </Col>
        </Row>
        <Row>
          <Col id="navigation">
            <MainNavigation
              isOpen={isMenuOpen}
              navigationItems={mainNavigationItems}
              onClose={closeMenu}
              trueFullScreen={false}
              user={currentUser}
            />
          </Col>
        </Row>
      </Container>
    </header>
  );
};
